import { DEFAULT_LOCALE, DELETION_FLAG, GroupKey, isDisabled } from "@packages/core";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useRef } from "react";
import { LuPlus } from "react-icons/lu";

import { ButtonStyle, PageName, TableStyle, TypographyStyle } from "#components";
import {
  useBedInfiniteQuery,
  useFacilityQuery,
  useGroupInfiniteQuery,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/groups/")({
  component: Component,
});

const classes = TableStyle.raw({ foot: false });

function Component() {
  const { facilityKey } = Route.useParams();

  const facilityQuery = useFacilityQuery(facilityKey);
  const groupInfiniteQuery = useGroupInfiniteQuery({
    index: "facilityKey-deleted",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const translate = useTranslate();

  const defaultGroupKey = facilityQuery.data?.groupKey;

  const groups = useMemo(
    function () {
      return groupInfiniteQuery.data?.pages
        .flatMap(function (page) {
          return page.groups;
        })
        .sort(function (a, b) {
          return a.groupKey === defaultGroupKey
            ? -1
            : b.groupKey === defaultGroupKey
              ? 1
              : a.name.localeCompare(b.name);
        });
    },
    [defaultGroupKey, groupInfiniteQuery.data],
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: groups?.length ?? 0,
    estimateSize() {
      return 64;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 20,
  });

  const gridTemplateColumns = "repeat(3, minmax(164px, 1fr)) 85px";
  const headCellClass = TypographyStyle({ color: "primary/60", ellipsis: true, fontWeight: 500 });

  return (
    <main
      className={css({
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "24px 16px", sm: "16px 24px" },
      })}
      data-maximized
    >
      <PageName>
        {translate("%GroupManagement.0%") +
          " - " +
          (!facilityQuery.isLoading
            ? (facilityQuery.data?.name ?? translate("%Unknown.0%"))
            : translate("%Loading.0%"))}
      </PageName>

      <h1
        className={css(TypographyStyle.raw({ ellipsis: true }), {
          fontSize: { smDown: "18px", sm: "20px" },
          fontWeight: 700,
        })}
      >
        {translate("%GroupManagement.0%")}
      </h1>

      {groups && groups.length > 0 ? (
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginTop: { smDown: "23px", sm: "16px" },
            minHeight: 0,
          })}
        >
          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "8px",
              justifyContent: "end",
            })}
          >
            <div className={css({ display: "flex", gap: "8px", minWidth: 0 })}>
              <Link
                className={ButtonStyle({
                  appearance: "ghost",
                  borderRadius: "4px",
                  color: "ghost",
                  fontSize: "14px",
                  gap: "8px",
                  minWidth: 0,
                  padding: "10px 16px",
                })}
                params={{ facilityKey }}
                to="/facilities/$facilityKey/groups/registration"
              >
                <LuPlus className={css({ height: "16px", width: "16px" })} />
                <span className={TypographyStyle({ ellipsis: true })}>{translate("%Add.1%")}</span>
              </Link>
            </div>
          </div>

          <div className={css({ flexGrow: 1, marginTop: "6px", minWidth: 0, overflowX: "auto" })}>
            <div
              className={css(
                classes.root,
                TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                {
                  height: "stretch",
                },
              )}
            >
              <div className={css(classes.head)}>
                <div className={css(classes.headRow)} style={{ gridTemplateColumns }}>
                  <div className={headCellClass}>{translate("%GroupName.0%")}</div>
                  <div className={headCellClass}>{translate("%NumberOfBeds.0%")}</div>
                  <div className={headCellClass}>{translate("%UpdateDate.0%")}</div>
                  <div />
                </div>
              </div>

              <div className={css(classes.virtualOuterBody)} ref={parentRef}>
                <div
                  className={css(classes.virtualInnerBody)}
                  style={{ height: virtualizer.getTotalSize() + "px" }}
                >
                  {virtualizer.getVirtualItems().map(function (virtualItem) {
                    const group = groups[virtualItem.index];
                    return (
                      <div
                        className={css(classes.virtualBodyRow)}
                        data-index={virtualItem.index}
                        key={virtualItem.key}
                        ref={virtualizer.measureElement}
                        style={{
                          gridTemplateColumns,
                          height: virtualItem.size + "px",
                          transform: "translateY(" + virtualItem.start + "px)",
                        }}
                      >
                        <div
                          className={cx(
                            TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 }),
                            isDisabled(group) &&
                              css({ color: "primary/60", textDecoration: "line-through" }),
                          )}
                        >
                          {group.name}
                        </div>
                        <NumberOfBedsCell groupKey={group.groupKey} />
                        <div
                          className={TypographyStyle({
                            ellipsis: true,
                            fontSize: "14px",
                            lineHeight: 1,
                          })}
                        >
                          {translate("%Date.1%", { $: group.updatedAt })}
                        </div>
                        <div className={css({ textAlign: "right" })}>
                          <Link
                            className={css(
                              ButtonStyle.raw({
                                appearance: "outlined",
                                borderRadius: "4px",
                                color: "outlined",
                                ellipsis: true,
                                flexGrow: 1,
                                fontSize: "14px",
                                gap: "8px",
                                padding: "12px",
                              }),
                              { '&[data-default-group="true"]': { display: "none" } },
                            )}
                            data-default-group={group.groupKey === defaultGroupKey}
                            params={{ facilityKey, groupKey: group.groupKey }}
                            to="/facilities/$facilityKey/groups/$groupKey"
                          >
                            <span className={TypographyStyle({ ellipsis: true })}>
                              {translate("%Details.0%")}
                            </span>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={css({
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: { sm: "center" },
            justifyContent: "center",
            gap: "12px",
          })}
        >
          <p className={TypographyStyle({ ellipsis: true, fontSize: "14px" })}>
            {translate("%NoData.3%", { $: { name: translate("%Group.0%") } })}
          </p>
          <Link
            className={ButtonStyle({
              appearance: "contained",
              borderRadius: "4px",
              color: "primary",
              fontSize: "14px",
              gap: "8px",
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/groups/registration"
          >
            <LuPlus className={css({ height: "16px", width: "16px" })} />
            <span className={TypographyStyle({ ellipsis: true })}>
              {translate("%Add.3%", { $: { name: translate("%Group.0%") } })}
            </span>
          </Link>
        </div>
      )}
    </main>
  );
}

function NumberOfBedsCell({ groupKey }: { groupKey: GroupKey }) {
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "groupKey-deleted",
    condition: [
      ["groupKey", "=", groupKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  return (
    <div
      className={TypographyStyle({
        ellipsis: true,
        fontSize: "14px",
        lineHeight: 1,
      })}
    >
      {bedInfiniteQuery.data?.pages
        .flatMap(function (page) {
          return page.beds;
        })
        .length.toLocaleString(DEFAULT_LOCALE)}
    </div>
  );
}
